import React, { ReactNode } from 'react';
import { ExclusiveAnd } from '../../util/types';
import { CreateIconComponentProps } from '../SvgIcons/CreateIconComponent';
import {
  Container,
  ContentContainer,
  TextContainer,
  Title,
  Description,
  StyledLink,
  basicIconStyles,
  StyledAlertIcon,
  StyledCheckmarkIcon,
  StyledRemoveIcon,
  StyledInfoFilledIcon,
} from './SectionMessage.styles';

// need either both href and alt, or neither
type SectionMessageAction = {
  /** Where the link points to */
  href: string;
  /** Text to be displayed for the link */
  alt: string;
};
type CustomIconType = {
  component: (p: CreateIconComponentProps) => JSX.Element;
  className?: string;
};

type SectionMessageCommonProps = {
  /** Determines color and icon, one of `'Confirmation'`, `'Warning'`, `'Error'`, or `'Info'` */
  type?: SectionMessageType;
  /** Title for the message */
  title: string;
  /** Main body for the message */
  description?: ReactNode;
  /** For showing an arrow after the hyperlink */
  arrow: boolean;
  /** Determines whether the icon should be hidden */
  hideIcon?: boolean;
  /** Optional tag component to be shown at the top of the section message */
  tagComponent?: JSX.Element;
  /** Custom icon; overrides the icon associated with a section message type */
  customIcon?: CustomIconType;
  /** Classname applied to container */
  className?: string;
};

export type SectionMessageProps = SectionMessageCommonProps & ExclusiveAnd<SectionMessageAction>;

export type SectionMessageType = 'Confirmation' | 'Warning' | 'Error' | 'Info';

/**
 * ```import { SectionMessage } from '@zola/zola-ui/src/components/SectionMessage';```
 *
 * Section Message component
 *
 *    - alerts users that something has happened in a section of the screen
 */
const SectionMessage: React.FC<SectionMessageProps> = ({
  type,
  title,
  description,
  href,
  alt,
  arrow,
  hideIcon,
  tagComponent,
  customIcon,
  className,
}) => {
  const getIconComponent = (type?: SectionMessageType, customIcon?: CustomIconType) => {
    if (customIcon) {
      const CustomIconComponent = customIcon.component;
      return <CustomIconComponent css={basicIconStyles} className={customIcon.className} />;
    }
    switch (type) {
      case 'Confirmation':
        return <StyledCheckmarkIcon />;
      case 'Error':
        return <StyledRemoveIcon />;
      case 'Warning':
        return <StyledAlertIcon />;
      default:
        return <StyledInfoFilledIcon />;
    }
  };

  return (
    <Container type={type} className={className}>
      {tagComponent}
      <ContentContainer addTopSpacing={Boolean(tagComponent)}>
        {!hideIcon && getIconComponent(type, customIcon)}
        <TextContainer>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
          {href && (
            <StyledLink sizes="small" href={href} arrow={arrow}>
              {alt}
            </StyledLink>
          )}
        </TextContainer>
      </ContentContainer>
    </Container>
  );
};

export default SectionMessage;
